import React, { useState } from 'react';
import { Modal, Checkbox, message } from 'antd';
import { useHookstate } from '@hookstate/core';
import { CreditCard, Zap, Lock, ArrowRight, Shield } from 'lucide-react';
import { usePostHog } from '@posthog/react';
import { useTheme } from '../../../../contexts/ThemeContext';
import clientActions from '../../../../actions/client';

const ALL_AMOUNTS = [5, 10, 25, 50, 100, 250, 500];

// Stripe fee constants (must match backend)
const STRIPE_PCT = 0.029;
const STRIPE_FIXED = 0.30;
const STRIPE_FEE_VAT = 0.05;
const calcGross = (net) => {
  const pctWithVAT = STRIPE_PCT * (1 + STRIPE_FEE_VAT);
  const fixedWithVAT = STRIPE_FIXED * (1 + STRIPE_FEE_VAT);
  return Math.ceil(((net + fixedWithVAT) / (1 - pctWithVAT)) * 100) / 100;
};

function TopupCard({ amountOwed = 0, showFees = false, canSkipAmountOwed = false }) {
  const posthog = usePostHog();
  const { colors } = useTheme();
  const [customMode, setCustomMode] = useState(false);
  const [skipOwed, setSkipOwed] = useState(false);

  const state = useHookstate({
    modalOpen: false,
    amount: 5,
    customAmount: '',
    agreedToTerms: false,
  });

  const handleOpenModal = () => state.modalOpen.set(true);

  const handleTopup = async () => {
    if (!state.agreedToTerms.get()) {
      message.warning('Please agree to the terms and conditions');
      return;
    }
    // Open a blank window synchronously so the browser trusts it as a user gesture.
    // Assigning location.href after the await avoids the popup blocker.
    const stripeWindow = window.open('', '_blank');
    try {
      const amount = state.amount.get();
      const data = await clientActions.requestTopup(amount, { skipAmountOwed: skipOwed });
      posthog?.capture('topup_initiated', { amount_uro: amount, amount_usd: amount });
      if (stripeWindow) {
        stripeWindow.location.href = data.url;
      } else {
        window.location.href = data.url;
      }
      state.modalOpen.set(false);
      state.agreedToTerms.set(false);
    } catch (err) {
      if (stripeWindow) stripeWindow.close();
      posthog?.capture('topup_failed', { amount_uro: state.amount.get(), error: err?.message });
      message.error(err?.response?.data?.error || 'Failed to create checkout session');
    }
  };

  const selectedAmount = state.amount.get();
  const effectiveOwed = (canSkipAmountOwed && skipOwed) ? 0 : amountOwed;
  const walletCredit = selectedAmount + effectiveOwed;
  const grossCharge = showFees ? calcGross(walletCredit) : walletCredit;
  const feeAmount = showFees ? (grossCharge - walletCredit) : 0;

  return (
    <>
      <div
        style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 16,
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: 20 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: 'linear-gradient(135deg, #10B981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CreditCard size={16} color="#fff" />
            </div>
            <span style={{ fontSize: 15, fontWeight: 600, color: colors.text }}>Add Credits</span>
          </div>

          {/* Promo / fee banner */}
          {showFees ? (
            <div
              style={{
                background: `${colors.text}06`,
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: '8px 12px',
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <CreditCard size={14} color={colors.textSecondary} />
              <span style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary }}>
                1 URO = $1 + payment processing fees
              </span>
            </div>
          ) : (
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(217,119,6,0.1))',
                border: '1px solid rgba(245,158,11,0.2)',
                borderRadius: 10,
                padding: '8px 12px',
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Zap size={14} color="#F59E0B" />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#F59E0B' }}>
                Limited time: 1 URO = $1 — we absorb all fees!
              </span>
            </div>
          )}

          {/* Amount pills */}
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 11,
                color: colors.textSecondary,
                marginBottom: 8,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Select amount
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ALL_AMOUNTS.map((v) => {
                const isSelected = !customMode && selectedAmount === v;
                return (
                  <button
                    key={v}
                    onClick={() => {
                      state.amount.set(v);
                      setCustomMode(false);
                    }}
                    className="topup-pill"
                    style={{
                      padding: '6px 14px',
                      borderRadius: 8,
                      border: `1px solid ${isSelected ? '#10B981' : colors.border}`,
                      background: isSelected ? 'rgba(16,185,129,0.12)' : 'transparent',
                      color: isSelected ? '#10B981' : colors.text,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {v}
                  </button>
                );
              })}
              <button
                onClick={() => setCustomMode(true)}
                className="topup-pill"
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  border: `1px solid ${customMode ? '#6366F1' : colors.border}`,
                  background: customMode ? 'rgba(99,102,241,0.1)' : 'transparent',
                  color: customMode ? '#6366F1' : colors.textSecondary,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                Custom
              </button>
            </div>
          </div>

          {/* Custom input */}
          {customMode && (
            <div style={{ marginBottom: 12 }}>
              <input
                type="number"
                min={5}
                max={10000}
                placeholder="Enter amount (5 – 10,000)"
                value={state.customAmount.get()}
                onChange={(e) => {
                  const val = e.target.value;
                  state.customAmount.set(val);
                  const num = parseInt(val);
                  if (num >= 5 && num <= 10000) state.amount.set(num);
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: `1px solid ${colors.border}`,
                  background: 'transparent',
                  color: colors.text,
                  fontSize: 14,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}

          {/* Skip amount owed toggle (special client bypass) */}
          {canSkipAmountOwed && amountOwed > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Checkbox
                checked={skipOwed}
                onChange={(e) => setSkipOwed(e.target.checked)}
              >
                <span style={{ fontSize: 13, color: colors.text }}>
                  Pay custom amount only (skip ${amountOwed.toFixed(2)} owed)
                </span>
              </Checkbox>
            </div>
          )}

          {/* Price preview */}
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              marginBottom: 14,
              background: 'rgba(16,185,129,0.06)',
              border: '1px solid rgba(16,185,129,0.12)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: effectiveOwed > 0 || showFees ? 4 : 0 }}>
              <span style={{ fontSize: 14, color: colors.text, fontWeight: 500 }}>
                {selectedAmount} URO Credits
              </span>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#10B981' }}>
                ${selectedAmount.toFixed(2)}
              </span>
            </div>
            {effectiveOwed > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#EF4444', fontWeight: 500 }}>
                  Sub-accounts owed
                </span>
                <span style={{ fontSize: 12, color: '#EF4444', fontWeight: 500 }}>
                  +${effectiveOwed.toFixed(2)}
                </span>
              </div>
            )}
            {showFees && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: colors.textSecondary }}>
                  Processing fee
                </span>
                <span style={{ fontSize: 11, color: colors.textSecondary }}>
                  +${feeAmount.toFixed(2)}
                </span>
              </div>
            )}
            {(effectiveOwed > 0 || showFees) && (
              <div style={{
                borderTop: `1px solid rgba(16,185,129,0.15)`,
                paddingTop: 6,
                marginTop: 4,
                display: 'flex',
                justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>
                  You pay
                </span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#10B981' }}>
                  ${grossCharge.toFixed(2)}
                </span>
              </div>
            )}
          </div>

          {/* CTA */}
          <button
            onClick={handleOpenModal}
            disabled={!(selectedAmount >= 5)}
            className="topup-cta-btn"
            style={{
              width: '100%',
              height: 44,
              borderRadius: 10,
              border: 'none',
              background:
                selectedAmount >= 5
                  ? 'linear-gradient(135deg, #10B981, #059669)'
                  : colors.border,
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: selectedAmount >= 5 ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              boxShadow:
                selectedAmount >= 5 ? '0 4px 14px rgba(16,185,129,0.25)' : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            Continue to Checkout
            <ArrowRight size={15} />
          </button>

          {/* Security badge */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              marginTop: 10,
            }}
          >
            <Lock size={11} color={colors.textSecondary} />
            <span style={{ fontSize: 11, color: colors.textSecondary }}>Secured by Stripe</span>
          </div>
        </div>
      </div>

      {/* Checkout Confirmation Modal */}
      <Modal
        open={state.modalOpen.get()}
        onCancel={() => {
          state.modalOpen.set(false);
          state.agreedToTerms.set(false);
        }}
        title={null}
        footer={null}
        width={440}
      >
        <div style={{ padding: '8px 0' }}>
          {/* Modal header */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #10B981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
              }}
            >
              <CreditCard size={20} color="#fff" />
            </div>
            <div style={{ fontSize: 17, fontWeight: 600 }}>Confirm Purchase</div>
          </div>

          {/* Promo */}
          {!showFees && (
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(217,119,6,0.08))',
                border: '1px solid rgba(245,158,11,0.2)',
                borderRadius: 10,
                padding: '8px 12px',
                marginBottom: 16,
                textAlign: 'center',
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600, color: '#F59E0B' }}>
                Special Offer: 1 URO = $1 (Limited time!)
              </span>
            </div>
          )}

          {/* Order summary */}
          <div
            style={{
              background: 'rgba(16,185,129,0.05)',
              border: '1px solid rgba(16,185,129,0.12)',
              borderRadius: 10,
              padding: 14,
              marginBottom: 14,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Order Summary</div>
            {[
              ['URO Credits', `${selectedAmount}`, false, false],
              ['Base amount', `$${selectedAmount.toFixed(2)} USD`, true, false],
              ...(amountOwed > 0
                ? [['Sub-accounts owed', `$${amountOwed.toFixed(2)}`, true, false]]
                : []),
              ...(amountOwed > 0
                ? [['Wallet credit', `$${walletCredit.toFixed(2)}`, false, false]]
                : []),
              showFees
                ? ['Processing fee', `$${feeAmount.toFixed(2)}`, true, false]
                : ['Payment fee', 'FREE (Limited time!)', true, true],
            ].map(([label, value, secondary, highlight], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: secondary ? colors.textSecondary : colors.text }}>
                  {label}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: highlight ? 500 : 400,
                    color: highlight ? '#10B981' : secondary ? colors.textSecondary : colors.text,
                  }}
                >
                  {value}
                </span>
              </div>
            ))}
            <div
              style={{
                borderTop: `1px solid ${colors.border}`,
                margin: '10px 0',
                paddingTop: 10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 600 }}>Total</span>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#10B981' }}>
                  {walletCredit.toFixed(2)} URO
                </span>
                <div style={{ fontSize: 11, color: colors.textSecondary }}>
                  ≈ ${grossCharge.toFixed(2)} USD
                </div>
              </div>
            </div>
          </div>

          {/* Notice */}
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              background: `${colors.text}06`,
              border: `1px solid ${colors.border}`,
              marginBottom: 14,
              fontSize: 12,
              color: colors.textSecondary,
              lineHeight: 1.7,
            }}
          >
            <div>
              • Credits are <strong style={{ color: colors.text }}>non-refundable</strong> once
              purchased
            </div>
            <div>• Taxes/VAT may apply based on billing address</div>
            <div>
              • By proceeding, you agree to our{' '}
              <a
                href="https://fluxuro.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#6366F1' }}
              >
                Terms
              </a>{' '}
              &{' '}
              <a
                href="https://fluxuro.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#6366F1' }}
              >
                Privacy Policy
              </a>
            </div>
          </div>

          {/* Terms checkbox */}
          <Checkbox
            checked={state.agreedToTerms.get()}
            onChange={(e) => state.agreedToTerms.set(e.target.checked)}
            style={{ marginBottom: 16 }}
          >
            <span style={{ fontSize: 12, color: colors.textSecondary }}>
              I agree to the terms and conditions
            </span>
          </Checkbox>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                state.modalOpen.set(false);
                state.agreedToTerms.set(false);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                background: 'transparent',
                color: colors.textSecondary,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleTopup}
              disabled={!state.agreedToTerms.get()}
              style={{
                padding: '8px 20px',
                borderRadius: 8,
                border: 'none',
                background: state.agreedToTerms.get()
                  ? 'linear-gradient(135deg, #10B981, #059669)'
                  : colors.border,
                color: '#fff',
                cursor: state.agreedToTerms.get() ? 'pointer' : 'not-allowed',
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.15s ease',
              }}
            >
              <Shield size={13} />
              Proceed to Stripe
            </button>
          </div>
        </div>
      </Modal>

      <style>{`
        .topup-cta-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(16,185,129,0.35) !important;
        }
        .topup-pill:hover {
          border-color: ${colors.text}30 !important;
        }
      `}</style>
    </>
  );
}

export default TopupCard;
